import { useState, useCallback, useRef } from 'react'
import { registrationService } from '@/services/registrationApi'
import type {
  ProfileType,
  GetRegistrationSurveyResponse,
  SubmitRegistrationRequest,
  SubmitRegistrationResponse,
  SurveyQuestion,
} from '@/types/registration'

interface UseRegistrationReturn {
  // Survey data
  survey: GetRegistrationSurveyResponse['survey'] | null
  questions: SurveyQuestion[]
  isLoadingSurvey: boolean
  surveyError: string | null
  loadSurvey: () => Promise<void>
  refetchSurvey: () => Promise<void>
  
  // Submission
  submit: (data: SubmitRegistrationRequest) => void
  submitAsync: (data: SubmitRegistrationRequest) => Promise<SubmitRegistrationResponse>
  isSubmitting: boolean
  submitError: string | null
  registrationResult: SubmitRegistrationResponse | null
  
  // Reset
  reset: () => void
  resetAll: () => void
}

/**
 * Custom hook for handling registration flow
 * Supports both dynamic survey-based and static form registration
 */
export function useRegistration(profileType: ProfileType): UseRegistrationReturn {
  // Survey state
  const [survey, setSurvey] = useState<GetRegistrationSurveyResponse['survey'] | null>(null)
  const [questions, setQuestions] = useState<SurveyQuestion[]>([])
  const [isLoadingSurvey, setIsLoadingSurvey] = useState(false)
  const [surveyError, setSurveyError] = useState<string | null>(null)
  
  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [registrationResult, setRegistrationResult] = useState<SubmitRegistrationResponse | null>(null)

  // Abort controller for canceling requests
  const abortControllerRef = useRef<AbortController | null>(null)

  /**
   * Load the registration survey for the profile type
   */
  const loadSurvey = useCallback(async () => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    setIsLoadingSurvey(true)
    setSurveyError(null)
    
    try {
      const response = await registrationService.getSurvey(profileType)
      setSurvey(response.survey)
      setQuestions(response.questions || [])
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('[useRegistration] Failed to load survey:', error)
        setSurveyError(error.message || 'Failed to load registration form')
      }
    } finally {
      setIsLoadingSurvey(false)
    }
  }, [profileType])

  /**
   * Refetch the survey (clears existing data first)
   */
  const refetchSurvey = useCallback(async () => {
    setSurvey(null)
    setQuestions([])
    await loadSurvey()
  }, [loadSurvey])

  /**
   * Submit registration data
   * For client: uses POST /api/users/register/ (new user + client profile).
   * For beneficiary/provider: uses POST /api/users/profiles/ (add profile to logged-in user).
   * Returns promise for async/await usage.
   */
  const submitAsync = useCallback(async (data: SubmitRegistrationRequest): Promise<SubmitRegistrationResponse> => {
    setIsSubmitting(true)
    setSubmitError(null)
    
    try {
      console.log('[useRegistration] Submitting registration:', {
        profile_type: data.profile_type,
        survey_id: data.survey_id,
        has_answers: !!data.answers,
      })
      
      const result = data.profile_type === 'client'
        ? await registrationService.registerClient(data)
        : await registrationService.submitRegistration(data)
      
      console.log('[useRegistration] Registration successful:', {
        user_id: result.user?.id,
        profile_id: result.profile?.id,
      })
      
      setRegistrationResult(result)
      return result
    } catch (error: any) {
      console.error('[useRegistration] Registration failed:', error)
      const errorMessage = error.message || 'Registration failed. Please try again.'
      setSubmitError(errorMessage)
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  /**
   * Submit registration (fire and forget)
   */
  const submit = useCallback((data: SubmitRegistrationRequest) => {
    submitAsync(data).catch(() => {
      // Error already handled in submitAsync
    })
  }, [submitAsync])

  /**
   * Reset submission state only
   */
  const reset = useCallback(() => {
    setSubmitError(null)
    setRegistrationResult(null)
  }, [])

  /**
   * Reset all state including survey
   */
  const resetAll = useCallback(() => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    
    setSurvey(null)
    setQuestions([])
    setSurveyError(null)
    setIsLoadingSurvey(false)
    setSubmitError(null)
    setRegistrationResult(null)
    setIsSubmitting(false)
  }, [])

  return {
    // Survey
    survey,
    questions,
    isLoadingSurvey,
    surveyError,
    loadSurvey,
    refetchSurvey,
    
    // Submission
    submit,
    submitAsync,
    isSubmitting,
    submitError,
    registrationResult,
    
    // Reset
    reset,
    resetAll,
  }
}

/**
 * Hook specifically for client registration
 */
export function useClientRegistration() {
  return useRegistration('client')
}

/**
 * Hook specifically for beneficiary registration
 */
export function useBeneficiaryRegistration() {
  return useRegistration('beneficiary')
}

/**
 * Hook specifically for provider registration
 */
export function useProviderRegistration() {
  return useRegistration('provider')
}
